// src/features/reabertura/components/ReaberturaTable.jsx
import React, { useMemo } from "react";
import styles from "./ReaberturaTable.module.css";

/**
 * ✅ Pega o ID numérico REAL da linha (sem “chutar” dígitos do código D2205).
 * Se não achar um ID numérico puro, retorna "" (e aí não deixa abrir modal).
 */
function getNumericId(r) {
  const candidates = [
    r?.id,
    r?.id_programacao,
    r?.programacao_id,

    // variações comuns em views/apis:
    r?.wf3_id,
    r?.id_wf3,
    r?.id_wf_3,

    r?.linha_id,
    r?.id_linha,
    r?.idLinha,
    r?.id_linhas,
    r?.idLinhaWf3,
  ];

  for (const v of candidates) {
    if (typeof v === "number" && Number.isFinite(v)) return String(Math.trunc(v));
    if (typeof v === "string") {
      const s = v.trim();
      if (/^\d+$/.test(s)) return s; // ✅ só aceita número puro
    }
  }
  return "";
}

function normUp(v) {
  return (v == null ? "" : String(v)).trim().toUpperCase();
}
function isFinal(row) {
  const finFlag = row?.is_finalizado;
  if (finFlag === 1 || finFlag === true || finFlag === "1" || finFlag === "true") return true;
  return normUp(row?.status_wf3) === "FINALIZADO";
}
function statusNorm(row) {
  const s = normUp(row?.status_wf3);
  if (isFinal(row)) return "FINALIZADO";
  if (s === "ABERTO") return "ABERTA";
  return s || "—";
}

/* sempre DD/MM/AAAA (inclusive quando vem YYYY-MM-DD) */
function fmtDateBR(v) {
  if (!v) return "";
  const s = String(v).trim();

  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;

  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return s;
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yy = d.getFullYear();
  return `${dd}/${mm}/${yy}`;
}

function fmtTime(v) {
  if (!v) return "";
  const s = String(v).trim();
  if (/^\d{2}:\d{2}:\d{2}/.test(s)) return s.slice(0, 5);
  if (/^\d{2}:\d{2}/.test(s)) return s.slice(0, 5);
  return s;
}
function fmtDT(v) {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const nn = String(d.getMinutes()).padStart(2, "0");
  return `${dd}/${mm}/${yyyy} ${hh}:${nn}`;
}
function fmtKm(v) {
  if (v == null || v === "") return "";
  const n = Number(String(v).replace(",", "."));
  if (!Number.isFinite(n)) return String(v);
  return n.toFixed(3);
}
function cellText(v) {
  if (v == null || v === "") return "—";
  const s = String(v);
  return s.trim() ? s : "—";
}

export default function ReaberturaTable({ rows = [], onReopen }) {
  const safe = Array.isArray(rows) ? rows : [];

  const COLS = useMemo(
    () => [
      { key: "codigo_unico", label: "Código", click: true, render: (r) => cellText(r?.codigo_unico) },
      { key: "codigo_unico_original", label: "Código original", render: (r) => cellText(r?.codigo_unico_original) },

      { key: "data", label: "Data", render: (r) => cellText(fmtDateBR(r?.data)) },

      { key: "gerencia", label: "Gerência", render: (r) => cellText(r?.gerencia) },
      { key: "coordenador", label: "Coordenador", render: (r) => cellText(r?.coordenador) },
      { key: "atividade", label: "Atividade", render: (r) => cellText(r?.atividade) },

      { key: "ativo", label: "Ativo", render: (r) => cellText(r?.ativo) },
      { key: "sub", label: "Sub", render: (r) => cellText(r?.sub) },
      { key: "kmInicio", label: "KM início", render: (r) => cellText(fmtKm(r?.kmInicio)) },
      { key: "kmFinal", label: "KM final", render: (r) => cellText(fmtKm(r?.kmFinal)) },

      { key: "equipamento", label: "Equipamento", render: (r) => cellText(r?.equipamento) },
      { key: "sbOrigem", label: "SB origem", render: (r) => cellText(r?.sbOrigem) },
      { key: "sbDeTrabalho", label: "SB de trabalho", render: (r) => cellText(r?.sbDeTrabalho) },
      { key: "sbEncerramento", label: "SB de encerramento", render: (r) => cellText(r?.sbEncerramento) },

      { key: "tempo_do_intervalo", label: "Tempo do intervalo", render: (r) => cellText(fmtTime(r?.tempo_do_intervalo)) },
      { key: "quantidade", label: "Quantidade", render: (r) => cellText(r?.quantidade) },
      { key: "os", label: "OS", render: (r) => cellText(r?.os) },
      { key: "unidade_de_medida", label: "Unidade de medida", render: (r) => cellText(r?.unidade_de_medida) },

      { key: "observacao", label: "Observação", long: true, render: (r) => cellText(r?.observacao) },
      { key: "nome_do_responsavel", label: "Nome do responsável", render: (r) => cellText(r?.nome_do_responsavel) },
      { key: "telefone_do_reponsavel", label: "Telefone do responsável", render: (r) => cellText(r?.telefone_do_reponsavel) },

      { key: "horario_do_intervalo", label: "Horário do intervalo", render: (r) => cellText(fmtTime(r?.horario_do_intervalo)) },
      { key: "horario_inicio_jornada_turma", label: "Horário do início da jornada da turma", render: (r) => cellText(fmtTime(r?.horario_inicio_jornada_turma)) },

      { key: "quantidade_maquinista", label: "Quantidade de maquinistas", render: (r) => cellText(r?.quantidade_maquinista) },
      { key: "maquinista", label: "Maquinista", render: (r) => cellText(r?.maquinista) },

      { key: "tipo_acionamento", label: "Tipo de acionamento", render: (r) => cellText(r?.tipo_acionamento) },
      { key: "locomotiva1", label: "Locomotiva 1", render: (r) => cellText(r?.locomotiva1) },
      { key: "locomotiva2", label: "Locomotiva 2", render: (r) => cellText(r?.locomotiva2) },

      { key: "prioridade", label: "Prioridade", render: (r) => cellText(r?.prioridade) },
      { key: "tipo_programacao", label: "Tipo de programação", render: (r) => cellText(r?.tipo_programacao) },
      { key: "responsavel_pelo_aceite", label: "Responsável pelo aceite", render: (r) => cellText(r?.responsavel_pelo_aceite) },

      { key: "primeira_previa", label: "Primeira prévia", long: true, render: (r) => cellText(r?.primeira_previa) },
      { key: "segunda_previa", label: "Segunda prévia", long: true, render: (r) => cellText(r?.segunda_previa) },

      { key: "primeiro_inicio", label: "1º Início", render: (r) => cellText(fmtTime(r?.primeiro_inicio)) },
      { key: "primeiro_fim", label: "1º Fim", render: (r) => cellText(fmtTime(r?.primeiro_fim)) },
      { key: "segundo_inicio", label: "2º Início", render: (r) => cellText(fmtTime(r?.segundo_inicio)) },
      { key: "segundo_fim", label: "2º Fim", render: (r) => cellText(fmtTime(r?.segundo_fim)) },
      { key: "terceiro_inicio", label: "3º Início", render: (r) => cellText(fmtTime(r?.terceiro_inicio)) },
      { key: "terceiro_fim", label: "3º Fim", render: (r) => cellText(fmtTime(r?.terceiro_fim)) },

      { key: "tempo_total", label: "Tempo total", render: (r) => cellText(fmtTime(r?.tempo_total)) },
      { key: "quantidade_real", label: "Quantidade real", render: (r) => cellText(r?.quantidade_real) },

      { key: "causador", label: "Causador", render: (r) => cellText(r?.causador) },
      { key: "causa_primaria", label: "Causa primária", render: (r) => cellText(r?.causa_primaria) },
      { key: "causa_secundaria", label: "Causa secundária", render: (r) => cellText(r?.causa_secundaria) },

      { key: "justificativa_fechamento", label: "Justificativa de fechamento", long: true, render: (r) => cellText(r?.justificativa_fechamento) },

      { key: "previsao_inicio_intervalo", label: "Previsão de início do intervalo", render: (r) => cellText(fmtTime(r?.previsao_inicio_intervalo)) },
      { key: "previsao_encerramento_jornada", label: "Previsão de encerramento da jornada", render: (r) => cellText(fmtTime(r?.previsao_encerramento_jornada)) },

      { key: "aceite_jornada_irregular", label: "Aceite de jornada irregular", render: (r) => cellText(r?.aceite_jornada_irregular) },
      { key: "quantidade_horas_irregulares", label: "Quantidade de horas irregulares", render: (r) => cellText(fmtTime(r?.quantidade_horas_irregulares)) },

      { key: "usuario_responsavel", label: "Usuário responsável", render: (r) => cellText(r?.usuario_responsavel) },
      { key: "criado_em", label: "Criado em", render: (r) => cellText(fmtDT(r?.criado_em)) },
      { key: "atualizado_em", label: "Atualizado em", render: (r) => cellText(fmtDT(r?.atualizado_em)) },

      { key: "status_wf3", label: "Status", badge: true, render: (r) => cellText(r?.status_wf3) },
    ],
    []
  );

  return (
    <div className={styles.wrap}>
      <div className={styles.scroller}>
        <div className={styles.tableInner}>
          <div className={styles.headerBg} aria-hidden />

          <table className={styles.table}>
            <thead>
              <tr>
                {COLS.map((c) => (
                  <th key={c.key} className={styles.th}>
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {safe.map((r, idx) => {
                const id = getNumericId(r);     // ✅ agora é robusto e seguro
                const fin = isFinal(r);
                const st = statusNorm(r);

                const key =
                  id ||
                  `${String(r?.codigo_unico || "row")}-${String(r?.data || "").slice(0, 10)}-${idx}`;

                let rowClass = styles.rowAberta;
                if (fin) rowClass = styles.rowFinalizado;
                else if (st === "REPROGRAMADO") rowClass = styles.rowReprogramado;
                else if (st === "EXECUTANDO") rowClass = styles.rowExecutando;

                // ✅ se tem id numérico, deixa abrir modal (o backend que valida finalizado)
                const canClick = !!id;

                return (
                  <tr
                    key={key}
                    className={`${rowClass} ${canClick ? styles.rowClickable : ""}`}
                    title={canClick ? "Clique para reabrir" : "Sem ID numérico na linha (não é possível reabrir)"}
                    onClick={() => {
                      if (canClick) onReopen?.(id);
                    }}
                    role={canClick ? "button" : undefined}
                    tabIndex={canClick ? 0 : -1}
                    onKeyDown={(e) => {
                      if (!canClick) return;
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onReopen?.(id);
                      }
                    }}
                  >
                    {COLS.map((c) => {
                      if (c.key === "codigo_unico") {
                        const value = c.render(r);
                        return (
                          <td
                            key={c.key}
                            className={`${styles.td} ${canClick ? styles.codeCell : ""}`}
                            onClick={(e) => {
                              if (!canClick) return;
                              e.stopPropagation();
                              onReopen?.(id);
                            }}
                          >
                            {value}
                          </td>
                        );
                      }

                      if (c.badge) {
                        let badge = styles.badgeOpen;
                        if (fin) badge = styles.badgeFinal;
                        else if (st === "REPROGRAMADO") badge = styles.badgeReprog;

                        return (
                          <td key={c.key} className={styles.td}>
                            <span className={badge}>{st || "—"}</span>
                          </td>
                        );
                      }

                      const text = c.render(r);
                      return (
                        <td key={c.key} className={styles.td}>
                          {text}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
